import React, { useState } from 'react';
import DecorationSpecs from 'src/pages/editor/components/scene/product/decorationSpecs';

/**
 * Persistent, collapsible drawer showing apparel decoration specifications and sizing data,
 * docked next to the canvas as a sibling of the tabbed side panel (`Panels`) rather than living
 * inside one of its tabs — so it stays visible no matter which tab (Scene Information, Scene
 * Layers, Scene Variation) the designer currently has open. See
 * ai-config/user-stories/apparel-positioning-and-auto-placement/summary.md.
 *
 * Open/closed state is local (not Redux) — this is purely a display preference for the current
 * session, same as e.g. the Advanced panel's own open/closed `Accordion` state elsewhere in the
 * app.
 *
 * Defaults to collapsed, not open — this always mounts (regardless of product type or whether
 * there's anything to show yet), so it always reserves at least its collapsed width against the
 * canvas. Defaulting to open would mean every session starts by shrinking the canvas for a panel
 * that, most of the time (any non-apparel product, or an apparel one nothing has resolved for
 * yet), has nothing to show — the designer has to actively expand it to find out. A previous
 * attempt instead tried to skip mounting this entirely when there was nothing to show yet, gating
 * on the product's resolved decoration specs — that made the drawer (and the canvas width around
 * it) visibly appear/disappear/reappear as that data resolved through its normal multiple stages
 * during ordinary SKU-picking, which was worse than just defaulting to collapsed.
 */
export default function DecorationSpecsDrawer() {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div
            className={`decoration-specs-drawer ${
                isOpen ? 'decoration-specs-drawer--open' : 'decoration-specs-drawer--collapsed'
            }`}
            data-testid='decoration-specs-drawer'>
            <button
                type='button'
                className='decoration-specs-drawer__toggle'
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? 'Collapse decoration specs panel' : 'Expand decoration specs panel'}
                aria-expanded={isOpen}>
                {isOpen ? '›' : '‹'}
                {!isOpen && <span className='decoration-specs-drawer__collapsed-label'>Decoration Specs</span>}
            </button>
            {isOpen && (
                <div className='decoration-specs-drawer__content'>
                    <h3 className='decoration-specs-drawer__title'>Apparel Decoration Specs</h3>
                    <DecorationSpecs />
                </div>
            )}
        </div>
    );
}
